import { useCallback, useMemo } from "react";
import type { HotelRoomCardRatesProps } from "@b2bportal/core-hotels";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
  type HotelRoomRateMeta,
  type OnSelectOptions,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { RadioGroup } from "@tenant/ui";
import clsx from "clsx";
import { useHotelRoomCardRates } from "./hooks";
import defaultStyles from "./HotelRoomCardRates.module.scss";
import { MealPlansToShow } from "./MealPlansToShow";
import { StrikethroughPrice } from "@/components/shared";

const COMPONENT_KEY = CoreHotelsComponent.HotelRoomCardRates;

/**
 * RBC's HotelRoomCardRates - uses Rate-based API.
 * Throws if rates are not provided.
 */
export const HotelRoomCardRates = (
  props: HotelRoomCardRatesProps & PlatformComponentProps
) => {
  if (
    props.rates == null ||
    props.selectedRate == null ||
    props.setSelectedRate == null
  ) {
    throw new Error(
      "RBC HotelRoomCardRates requires rates, selectedRate, and setSelectedRate props."
    );
  }
  const { t } = useI18nContext();
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const {
    context,
    onSelectBaseRateId,
    nightlyPrice,
    totalPrice,
    totalNotDiscountedPrice,
    nightCount,
    roomCount,
    nightlyPoints,
    totalPoints,
    showPointsPricing,
    mealPlanRatesForBaseRate,
    showMealPlans,
  } = useHotelRoomCardRates({
    rates: props.rates,
    selectedRate: props.selectedRate,
    nightCount: props.nightCount,
    setSelectedRate: props.setSelectedRate,
  });

  const { baseRates, selectedBaseRate } = context;

  const createOnSelect = useCallback(
    (rate: HotelRoomRateMeta) => (options?: OnSelectOptions) => {
      props.setSelectedRate!(rate);
      if (options?.selectRoom && props.setSelectedRoom != null) {
        props.setSelectedRoom(rate.product);
      }
    },
    [props.setSelectedRate, props.setSelectedRoom]
  );

  const baseRatesRadioGroup = useMemo(() => {
    if (baseRates.length === 0) return null;

    // Within the active cancellation-policy group, the user may have picked a
    // meal plan other than the group's representative rate. Render that
    // meal plan's own View/product (so CFAR coverage + merchandising content
    // reflect the chosen meal plan) but keep priceDifference pinned to the
    // representative rate's, since the CFAR premium shown shouldn't change
    // based on meal plan.
    const getContentRate = (rate: HotelRoomRateMeta) =>
      rate.key === selectedBaseRate.key ? context.selectedRate : rate;

    if (baseRates.length === 1) {
      const rate = baseRates[0];
      const contentRate = getContentRate(rate);
      return (
        <contentRate.View
          product={contentRate.product}
          priceDifference={rate.priceDifference}
          isSelected={true}
          onSelect={createOnSelect(rate)}
        />
      );
    }

    const radioOptions = baseRates.map((rate) => {
      const contentRate = getContentRate(rate);
      return {
        value: rate.key,
        label: (
          <contentRate.View
            product={contentRate.product}
            priceDifference={rate.priceDifference}
            isSelected={selectedBaseRate.key === rate.key}
            key={rate.key}
            onSelect={createOnSelect(rate)}
          />
        ),
        id: rate.key,
      };
    });

    return (
      <RadioGroup
        className={cn("rate-radio-group")}
        options={radioOptions}
        defaultValue={selectedBaseRate.key}
        onSelectValue={onSelectBaseRateId}
      />
    );
  }, [
    baseRates,
    cn,
    onSelectBaseRateId,
    selectedBaseRate.key,
    createOnSelect,
    context.selectedRate,
  ]);

  return (
    <div className={clsx(block, props.className)}>
      {baseRates.length > 1 && (
        <p className={cn("title")}>
          {`${t("lodgingShopDetails.chooseCancellationPolicy")}:`}
        </p>
      )}
      <div className={cn("rates", ["cancellation"])}>{baseRatesRadioGroup}</div>
      {showMealPlans && (
        <>
          <p className={cn("title")}>
            {`${t("lodgingShopDetails.chooseMealPlan")}:`}
          </p>
          <div className={cn("rates", ["meal-plans"])}>
            <MealPlansToShow
              rates={mealPlanRatesForBaseRate}
              cn={cn}
              selectedRate={context.selectedRate}
              setSelectedRate={props.setSelectedRate}
            />
          </div>
        </>
      )}
      <div className={cn("divider")} />
      <div className={cn("price-container")}>
        <div className={cn("room-price")}>
          <div className={cn("label")}>
            {t("pricePerNightLabel")}
            {roomCount > 1 &&
              ` - ${t(
                "core-hotels.availabilityPage.results.hotelCard.price.numberOfRooms",
                {
                  count: roomCount,
                }
              )}`}
          </div>
          <div className={cn("value")}>
            {showPointsPricing ? nightlyPoints : nightlyPrice}
          </div>
        </div>
        <div className={cn("total-price")}>
          <div className={cn("label")}>
            {t("totalPriceLabel", { count: nightCount })}
            {roomCount > 1 &&
              ` - ${t(
                "core-hotels.availabilityPage.results.hotelCard.price.numberOfRooms",
                {
                  count: roomCount,
                }
              )}`}
          </div>
          <div className={cn("value")}>
            {totalNotDiscountedPrice && (
              <StrikethroughPrice price={totalNotDiscountedPrice} withMargin />
            )}
            {showPointsPricing ? totalPoints : totalPrice}
          </div>
        </div>
        <p className={cn("helper-text")}>
          {showPointsPricing
            ? t(
                "core-hotels.availabilityPage.results.hotelCard.price.totalPriceDescription",
                { price: totalPrice }
              )
            : t("taxesAndFeesIncl")}
        </p>
      </div>
      {props.children}
    </div>
  );
};
